import React, { useState, useEffect, useRef } from 'react';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useProductTracking } from '../../hooks/useProductTracking';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as farHeart } from '@fortawesome/free-regular-svg-icons';
import { faHeart as fasHeart, faInfoCircle, faTag, faCartPlus, faStar, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import './ProductCard.css';
import { Modal, Button } from 'react-bootstrap';
import OptimizedImage from '../UI/OptimizedImage';
import { generateImageAltText, generateImageTitle } from '../../utils/productHelpers';
import toastService from '../../services/toastService';

const ProductCard = ({ product, isFavorite: propIsFavorite, onAddToCartSuccess, defaultSelectedModel }) => {
  const navigate = useNavigate();
  const [selectedModel, setSelectedModel] = useState(defaultSelectedModel || '');
  const isManualSelectionRef = useRef(false); // Kullanıcının manuel seçim yapıp yapmadığını takip et (ref kullanarak closure sorununu önle)
  const [selectedColor, setSelectedColor] = useState(null);
  const [availableColors, setAvailableColors] = useState([]);
  const [showColorInfo, setShowColorInfo] = useState(false);
  const [loadingColors, setLoadingColors] = useState(false);
  const { addToCart, cart } = useCart();
  const { favorites, addToFavorites, removeFromFavorites } = useFavorites();
  const { trackProductView, trackProductClick, trackProductAddToCart, trackProductAddToFavorites, trackProductRemoveFromFavorites } = useProductTracking();
  const [localIsFavorite, setLocalIsFavorite] = useState(propIsFavorite);
  const [showStockWarning, setShowStockWarning] = useState(false);
  const [stockWarningMessage, setStockWarningMessage] = useState('');
  const [stockStatus, setStockStatus] = useState({
    isChecking: false,
    hasStockIssue: false,
    message: ''
  });

  // Ürün görüntüleme tracking - sadece component mount olduğunda
  useEffect(() => {
    if (product) {
      trackProductView(product);
    }
  }, []); // Sadece mount olduğunda çalışsın

  // defaultSelectedModel prop'u değiştiğinde selectedModel'i güncelle
  // normalizePhoneModels fonksiyonu component içinde daha aşağıda tanımlı
  // NOT: Kullanıcı manuel seçim yaptıysa (isManualSelectionRef.current = true), defaultSelectedModel değişse bile override etme
  useEffect(() => {
    // Kullanıcı manuel seçim yaptıysa, defaultSelectedModel değişikliklerini görmezden gel
    if (isManualSelectionRef.current) {
      return;
    }

    if (defaultSelectedModel && product) {
      // normalizePhoneModels fonksiyonunu burada tanımla (component içindeki versiyonunu kullanmadan önce)
      const getPhoneModels = (product) => {
        if (!product) return [];
        if (product.phone_models && Array.isArray(product.phone_models)) {
          return product.phone_models.map(item => ({
            id: item.id,
            brand: item.brand || (item.name ? item.name.split(' ')[0] : ''),
            model_name: item.model_name || (item.name ? item.name.replace(item.brand || item.name.split(' ')[0], '').trim() : ''),
            name: item.name,
            is_active: true
          }));
        }
        if (product.phoneModels && Array.isArray(product.phoneModels)) {
          return product.phoneModels;
        }
        if (product.product_phone_models) {
          return product.product_phone_models.map(item => ({
            id: item.phone_model_id,
            brand: item.phone_models.brand,
            model_name: item.phone_models.model_name,
            is_active: item.phone_models.is_active
          }));
        }
        return [];
      };
      
      const phoneModels = getPhoneModels(product);
      const activePhoneModels = phoneModels.filter(model => model.is_active);
      
      // Model aktif mi kontrol et
      const modelExists = activePhoneModels.some(model => model.id === parseInt(defaultSelectedModel));
      if (modelExists) {
        isManualSelectionRef.current = false; // defaultSelectedModel'den geldiği için manuel seçim değil
        setSelectedModel(defaultSelectedModel);
      }
    } else if (!defaultSelectedModel) {
      // Eğer defaultSelectedModel kaldırıldıysa ve kullanıcı manuel seçim yapmamışsa temizle
      if (!isManualSelectionRef.current) {
        setSelectedModel('');
      }
    }
  }, [defaultSelectedModel, product]);

  // Telefon modeli değiştiğinde renk seçeneklerini güncelle
  useEffect(() => {
    const fetchColorOptions = async () => {
      if (!selectedModel) {
        setAvailableColors([]);
        setSelectedColor(null);
        setStockStatus({
          isChecking: false,
          hasStockIssue: false,
          message: ''
        });
        return;
      }

      try {
        // Renk seçeneklerinin yüklendiğini belirt
        setLoadingColors(true);
        setStockStatus({
          isChecking: true,
          hasStockIssue: false,
          message: 'Stok bilgisi kontrol ediliyor...'
        });
        
        // Seçilen telefon modeli için renk seçeneklerini getir (product_type'a göre)
        const productTypeId = product?.product_type_id;
        const apiUrl = productTypeId 
          ? `/phone-models/${selectedModel}/colors?product_type_id=${productTypeId}`
          : `/phone-models/${selectedModel}/colors`;
        const response = await api.get(apiUrl);
        if (response.data && response.data.length > 0) {
          // API'den gelen renk verilerini zenginleştir
          const enrichedColors = response.data.map(color => ({
            ...color,
            hex_code: color.code || color.hex_code, // code alanını hex_code olarak kullan
            checkedStock: false, // Stok kontrolü yapılıp yapılmadığını belirtmek için
            actualStock: null // Ürüne özgü gerçek stok bilgisi
          }));
          
          // Her renk için gerçek stok bilgisini al
          const colorsWithStock = await Promise.all(
            enrichedColors.map(async (color) => {
              try {
                // Ürün, model ve renk için stok kontrolü
                const stockResponse = await api.get(`/products/${product.id}/models/${selectedModel}/colors/${color.id}/stock`);
                return {
                  ...color,
                  checkedStock: true,
                  actualStock: stockResponse.data.stock || 0,
                  stock: stockResponse.data.stock || 0 // Orijinal stok değerini güncelle
                };
              } catch (error) {
                console.error(`Error checking stock for color ${color.name}:`, error);
                return {
                  ...color,
                  checkedStock: true,
                  actualStock: 0,
                  stock: 0 // Hata durumunda stok 0 olarak işaretle
                };
              }
            })
          );
          
          setAvailableColors(colorsWithStock);
          setSelectedColor(null); // Renk seçimini sıfırla
          
          // Sepetteki ürünlerin stok kontrolünü yap
          await checkCartStockForModel(selectedModel, colorsWithStock);
        } else {
          setAvailableColors([]);
          setSelectedColor(null);
          setStockStatus({
            isChecking: false,
            hasStockIssue: true,
            message: 'Bu model için renk seçeneği bulunmuyor'
          });
        }
      } catch (err) {
        console.error('Error fetching color options:', err);
        setAvailableColors([]);
        setSelectedColor(null);
        setStockStatus({
          isChecking: false,
          hasStockIssue: true,
          message: 'Stok bilgisi alınamadı'
        });
      } finally {
        // Yükleme işlemi tamamlandı
        setLoadingColors(false);
      }
    };

    if (product && selectedModel) {
      fetchColorOptions();
    }
  }, [selectedModel, cart, product]);

  // Seçilen model için sepetteki miktarı kontrol et ve stok uyarısı göster
  const checkCartStockForModel = async (modelId, colors) => {
    if (!colors || colors.length === 0) return;
    
    try {
      // Her renk için sepetteki öğeleri kontrol et
      const colorsWithCartInfo = await Promise.all(
        colors.map(async (color) => {
          // Bu ürün, model ve renk için sepetteki öğeleri bul
          const cartItemsWithSameModelAndColor = cart.filter(item => {
            const itemModelId = item.selectedModel?.id || item.phone_model?.id || item.phone_model_id;
            const itemColorId = item.selectedColor?.id || item.color?.id || item.color_id;
            return Number(itemModelId) === Number(modelId) && Number(itemColorId) === Number(color.id);
          });
          
          // Sepetteki toplam miktar
          const cartQuantity = cartItemsWithSameModelAndColor.reduce((sum, item) => sum + item.quantity, 0);
          
          // API'den alınan stok miktarı
          const availableStock = color.stock || 0;
          
          // Kalan stok miktarı
          const remainingStock = Math.max(0, availableStock - cartQuantity);
          
          return {
            ...color,
            cartQuantity,
            remainingStock,
            hasCartStock: cartQuantity > 0,
            isStockFull: cartQuantity >= availableStock
          };
        })
      );
      
      // Modelin toplam stok miktarı
      const totalModelStock = colorsWithCartInfo.reduce((sum, color) => sum + (color.stock || 0), 0);
      
      // Modelin toplam sepet miktarı
      const totalCartQuantity = colorsWithCartInfo.reduce((sum, color) => sum + color.cartQuantity, 0);
      
      // Tamamen tükenmiş renklerin sayısı
      const outOfStockColors = colorsWithCartInfo.filter(color => color.isStockFull).length;
      
      // Stok tükendiyse veya tüm stok sepetteyse uyarı göster
      if (totalCartQuantity >= totalModelStock) {
        setShowStockWarning(true);
        setStockWarningMessage(`Bu telefon modeline ait tüm renklerin stok limiti dolmuştur. (${totalCartQuantity}/${totalModelStock})`);
        setStockStatus({
          isChecking: false,
          hasStockIssue: true,
          message: `Sepetinizde bu modelden yeterli sayıda ürün bulunuyor! (${totalCartQuantity}/${totalModelStock})`
        });
      } else if (outOfStockColors > 0 && outOfStockColors === colorsWithCartInfo.length) {
        // Tüm renkler tükenmişse
        setStockStatus({
          isChecking: false,
          hasStockIssue: true,
          message: 'Bu model için sepetinizde yeterli sayıda ürün bulunuyor!'
        });
      } else if (totalModelStock === 0) {
        // Modelde hiç stok yoksa
        setStockStatus({
          isChecking: false,
          hasStockIssue: true,
          message: 'Bu model için stokta ürün bulunmuyor!'
        });
      } else {
        // Her şey normal, renk seçimine devam et
        setShowStockWarning(false);
        setStockWarningMessage('');
        setStockStatus({
          isChecking: false,
          hasStockIssue: false,
          message: ''
        });
        
        // Güncellenen stok bilgisini içeren renkleri tekrar set et
        setAvailableColors(colors.map(color => {
          const colorInfo = colorsWithCartInfo.find(c => c.id === color.id);
          if (colorInfo) {
            return {
              ...color,
              stock: colorInfo.remainingStock, // Kalan stok bilgisi
              cartQuantity: colorInfo.cartQuantity,
              originalStock: color.stock // Orijinal stok bilgisini sakla
            };
          }
          return color;
        }));
      }
    } catch (error) {
      console.error('Cart stock check error:', error);
      setStockStatus({
        isChecking: false,
        hasStockIssue: true,
        message: 'Stok kontrolü sırasında bir hata oluştu'
      });
    }
  };

  if (!product) return null;

  const formatPrice = (price) => {
    return price?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleCardClick = () => {
    trackProductClick(product);
    navigate(`/product/${product.id}`);
  };

  const handleColorSelect = (e, color) => {
    e.stopPropagation();
    // Kalan stok veya orijinal stok değerine bakılarak kontrol yapılır
    const availableStock = color.remainingStock !== undefined ? color.remainingStock : color.stock;
    
    // Stokta olmayan renkleri seçmeyi engelle
    if (availableStock <= 0) {
      const inCartMessage = color.cartQuantity > 0 
        ? `${color.name} rengi sepetinizde bulunmaktadır! (${color.cartQuantity}/${color.originalStock || color.stock})`
        : `${color.name} rengi için stok bulunmamaktadır!`;
        
      toast.error(inCartMessage);
      return;
    }
    setSelectedColor(color);
  };

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    trackProductAddToCart(product);
    try {
      // Telefon modeli seçilmemiş
      if (phoneModels?.length > 0 && !selectedModel) {
        toastService.showModelNotSelectedToast();
        return;
      }

      // Renk seçenekleri yükleniyorsa işlemi engelle
      if (loadingColors) {
        toastService.showColorsLoadingToast();
        return;
      }

      // Modele ait renk yoksa işlemi engelle
      if (availableColors.length === 0) {
        toast.warn('Bu model için renk seçeneği bulunmuyor');
        return;
      }

      // Renk seçilmemiş
      if (availableColors.length > 0 && !selectedColor) {
        toastService.showColorNotSelectedToast();
        return;
      }

      // Stok kontrolü
      if (selectedColor && selectedColor.stock <= 0) {
        toast.error('Bu ürün stokta bulunmamaktadır!');
        return;
      }

      // Seçilen modelin tam bilgilerini bul
      const selectedPhoneModel = phoneModels.find(model => model.id === parseInt(selectedModel));
      
      if (selectedModel && !selectedPhoneModel) {
        toastService.showModelNotFoundToast();
        return;
      }

      // Tüm kontroller geçildi, sepete ekle
      await addToCart(product, selectedPhoneModel, selectedColor);
      
      // Ürün sepete ekleme tracking
      trackProductAddToCart(product);
      
      // Sidebar'ı aç
      if (onAddToCartSuccess) {
        onAddToCartSuccess();
      }
    } catch (error) {
      // Hata mesajı göster
      toastService.showCartErrorToast(error.message || 'Sepete eklenirken bir hata oluştu');
    }
  };

  const isFavorite = localIsFavorite || favorites.some(fav => fav.product?.id === product.id);
  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    try {
      if (isFavorite) {
        await removeFromFavorites(product.id);
        trackProductRemoveFromFavorites(product);
        setLocalIsFavorite(false);
        toast.success('Ürün favorilerden kaldırıldı');
      } else {
        await addToFavorites(product);
        trackProductAddToFavorites(product);
        setLocalIsFavorite(true);
        toast.success('Ürün favorilere eklendi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const normalizePhoneModels = (product) => {
    if (!product) return [];
    
    // Önce filtrelenmiş phone_models array'ini kontrol et (product_type'a göre filtrelenmiş)
    if (product.phone_models && product.phone_models.length > 0) {
      return product.phone_models.map(item => ({
        id: item.id,
        brand: item.brand || (item.name ? item.name.split(' ')[0] : ''),
        model_name: item.model_name || (item.name ? item.name.replace(item.brand || item.name.split(' ')[0], '').trim() : ''),
        name: item.name,
        is_active: true // phone_models array'indeki tüm modeller zaten aktif
      }));
    }
    
    // Fallback: Eski product_phone_models array'ini kullan (filtrelenmemiş)
    if (product.product_phone_models) {
      return product.product_phone_models.map(item => ({
        id: item.phone_model_id,
        brand: item.phone_models.brand,
        model_name: item.phone_models.model_name,
        is_active: item.phone_models.is_active
      }));
    }

    return [];
  };

  const phoneModels = normalizePhoneModels(product);
  const activePhoneModels = phoneModels.filter(model => model.is_active);

  // If there are phone models but none are active, don't show the card
  if (phoneModels.length > 0 && activePhoneModels.length === 0) {
    return null;
  }

  const handleColorInfoClick = (e) => {
    e.stopPropagation();
    setShowColorInfo(true);
  };

  const handleCloseColorInfo = () => {
    setShowColorInfo(false);
  };

  // Stok uyarısı modalını kapat
  const handleCloseStockWarning = () => {
    setShowStockWarning(false);
  };

  const handleImageError = () => {
    // You can add custom error handling logic here if needed
    console.log(`Image loading error for product: ${product.id}`);
  };

  return (
    <>
      <div className="product-card" onClick={handleCardClick}>
        <div className="image-container">
          <OptimizedImage 
            src={product.images}
            alt={generateImageAltText(product) || `${product.name} - Telefon Kılıfı`}
            title={generateImageTitle(product) || `${product.name} - Mr. Brown Case`}
            className="product-image"
            onError={handleImageError}
            priority={false}
          />
          <button 
            className={`favorite-button ${isFavorite ? 'is-favorite' : ''}`}
            onClick={handleFavoriteClick}
            aria-label={isFavorite ? 'Favorilerden çıkar' : 'Favorilere ekle'}
          >
            <FontAwesomeIcon 
              icon={isFavorite ? fasHeart : farHeart} 
              className="favorite-icon" 
            />
          </button>
          
          {/* İndirim Etiketi */}
          <div className="discount-badge">
            <FontAwesomeIcon icon={faTag} className="discount-icon" />
            <span className="discount-text">%33 İndirim</span>
          </div>
          
          {/* Öne Çıkan Etiketi - Geliştirilmiş kontrol */}
          {(product.is_featured === true || product.is_featured === 1 || product.is_featured === 'true') && (
            <div className="featured-badge">
              <FontAwesomeIcon icon={faStar} className="featured-icon" />
              <span className="featured-text">Öne Çıkan</span>
            </div>
          )}
        </div>
        <div className="card-body">
          <h5 className="product-title">{product.name}</h5>
          
          {phoneModels && phoneModels.length > 0 && (
            <div className="models-container">
              <span className="models-label">Uyumlu Modeller:</span>
              <select 
                className="model-select"
                value={selectedModel}
                onChange={(e) => {
                  e.stopPropagation();
                  setSelectedModel(e.target.value);
                  isManualSelectionRef.current = true; // Kullanıcı manuel olarak seçim yaptı
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <option value="">Model seçin</option>
                {phoneModels.filter(model => model.is_active).map(model => (
                  <option key={model.id} value={model.id}>
                    {model.brand || ''} {model.model_name || model.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Stok durumu mesajı */}
          {selectedModel && stockStatus.hasStockIssue && (
            <div className="stock-status-message warning" onClick={(e) => e.stopPropagation()}>
              <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
              <span>{stockStatus.message}</span>
            </div>
          )}

          {selectedModel && (loadingColors || stockStatus.isChecking) ? (
            <div className="colors-container loading">
              <div className="colors-label-container">
                <span className="colors-label">Renk Seçenekleri Yükleniyor...</span>
              </div>
              <div className="color-loading">
                <div className="color-loading-indicator"></div>
              </div>
            </div>
          ) : availableColors.length > 0 && (
            <div className="colors-container">
              <div className="colors-label-container">
                <span className="colors-label">Çerçeve Rengi:</span>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="color-info-icon" 
                  onClick={handleColorInfoClick}
                />
              </div>
              <div className="color-options">
                {availableColors.map(color => {
                  const colorCode = color.code || color.hex_code;
                  const isWhiteColor = colorCode === '#FFFFFF' || colorCode === 'white' || colorCode === '#FFF';
                  const isTransparentColor = colorCode === 'transparent' || colorCode === 'rgba(0,0,0,0)';
                  const isDarkColor = colorCode === '#000000' || colorCode === 'black' || colorCode === '#000';
                  const isLightColor = isWhiteColor || isTransparentColor;
                  const isOutOfStock = color.stock <= 0;
                  
                  return (
                    <button
                      key={color.id}
                      className={`color-option ${selectedColor?.id === color.id ? 'selected' : ''} 
                        ${isTransparentColor ? 'transparent-color' : ''}
                        ${isWhiteColor ? 'white-color' : ''} 
                        ${isOutOfStock ? 'out-of-stock' : ''}`}
                      style={{ 
                        backgroundColor: colorCode,
                        border: isLightColor ? '2px solid #ddd' : 'none',
                        opacity: isOutOfStock ? 0.6 : 1
                      }}
                      onClick={(e) => handleColorSelect(e, color)}
                      title={`${color.name}${isOutOfStock ? ' (Tükendi)' : ''}`}
                    >
                      {isTransparentColor && <div className="transparent-pattern"></div>}
                      {selectedColor?.id === color.id && (
                        <span className={`color-check ${isLightColor ? 'dark-check' : ''} ${isDarkColor ? 'light-check' : ''}`}>
                          ✓
                        </span>
                      )}
                      {isOutOfStock && (
                        <div className="stock-status-overlay">
                          Tükendi
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {selectedColor && (
                <div className="selected-color-name">
                  <span>{selectedColor.name}</span>
                </div>
              )}
            </div>
          )}

          <div className="price-container">
            <span className="price">{formatPrice(product.price)} TL</span>
          </div>

          <button 
            className={`add-to-cart-btn ${(stockStatus.hasStockIssue || (selectedColor && selectedColor.stock <= 0)) ? 'disabled' : ''}`}
            onClick={handleAddToCart}
            disabled={stockStatus.hasStockIssue || (selectedColor && selectedColor.stock <= 0)}
          >
            <FontAwesomeIcon icon={faCartPlus} className="cart-icon" />
            <span>Sepete Ekle</span>
          </button>
        </div>
      </div>

      {/* Color Info Modal */}
      <Modal 
        show={showColorInfo} 
        onHide={handleCloseColorInfo} 
        centered
        className="color-info-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Renk Seçenekleri Hakkında</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Değerli Müşterilerimiz,</p>
          <p>Sublimasyon baskı ile üretilen sert plastik telefon kılıflarımız, yalnızca beyaz, siyah ve transparan yan kenar renklerine sahiptir.</p>
          <ul>
            <li><strong>Beyaz Kılıflar:</strong> Arka yüzeyi baskıya uygundur, yan kenarları beyazdır.</li>
            <li><strong>Siyah Kılıflar:</strong> Arka yüzeyi baskıya uygundur, yan kenarları siyahtır.</li>
            <li><strong>Transparan Kılıflar:</strong> Arka yüzeyi baskıya uygundur, yan kenarları şeffaftır ve telefonunuzun rengini gösterir.</li>
          </ul>
          <p>Baskı işlemi yalnızca kılıfın arka yüzeyine uygulanmaktadır. Yan kenarlara herhangi bir baskı yapılmamaktadır. Sipariş verirken bu bilgiyi göz önünde bulundurmanızı rica ederiz.</p>
          <p>Anlayışınız için teşekkür eder, keyifli alışverişler dileriz!</p>
          <p><strong>Mr Brown Case</strong></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseColorInfo}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Stock Warning Modal */}
      <Modal 
        show={showStockWarning} 
        onHide={handleCloseStockWarning} 
        centered
        className="stock-warning-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Stok Uyarısı</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Uyarı!</p>
          <p>{stockWarningMessage}</p>
          <p>Sepetinizde bulunan aynı modeldeki ürünlerden dolayı stok limiti dolmuştur. Lütfen önce sepetinizi kontrol ediniz.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseStockWarning}>
            Kapat
          </Button>
          <Button variant="primary" onClick={() => navigate('/cart')}>
            Sepete Git
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProductCard; 